import { useState, useEffect, useCallback } from 'react';
import { boardsApi } from '../../api';
import { getQuarterLabels } from '../../components/board/HitsPanel';
import { useBoardEvents } from '../../hooks/useBoardEvents';
import type { Board, Hit } from '../../types';

export interface WinnerGroup {
  label: string;
  shortLabel: string;
  mainHit: Hit;
  wrongWayHit?: Hit;
  plusNHit?: Hit;
  plusNWrongWayHit?: Hit;
  totalPayout: number;
}

export const usePublicBoardWinners = (boardSlug: string | undefined) => {
  const [board, setBoard] = useState<Board | null>(null);
  const [winnerGroups, setWinnerGroups] = useState<WinnerGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (showLoading = false) => {
    if (!boardSlug) return;
    if (showLoading) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const [boardData, hitsData] = await Promise.all([
        boardsApi.getPublic(boardSlug),
        boardsApi.getPublicHits(boardSlug),
      ]);
      setBoard(boardData);

      // Group hits by quarter
      const otEnabled =
        boardData.payoutConfig?.overtimeConfig?.enabled ?? false;
      const quarterLabels = getQuarterLabels(boardData.quarters, otEnabled);

      // Get main hits (not wrong-way, not plusN, has a quarter)
      const mainHits = hitsData.filter(
        (h) =>
          !h.isWrongWay &&
          !h.isPlusN &&
          !h.categoryId &&
          h.quarter !== undefined &&
          h.quarter !== null
      );

      const groups: WinnerGroup[] = mainHits.map((mainHit) => {
        // Find associated wrong-way and plusN hits
        const wrongWayHit = hitsData.find(
          (h) =>
            h.parentHitId === mainHit.id && h.isWrongWay && !h.isPlusN
        );
        const plusNHit = hitsData.find(
          (h) =>
            h.parentHitId === mainHit.id && h.isPlusN && !h.isWrongWay
        );
        const plusNWrongWayHit = hitsData.find(
          (h) =>
            h.parentHitId === mainHit.id && h.isPlusN && h.isWrongWay
        );

        // Find label
        const ql = quarterLabels.find((q) => q.quarter === mainHit.quarter);
        const label = ql?.label ?? `Q${mainHit.quarter}`;
        const shortLabel = ql?.shortLabel ?? `Q${mainHit.quarter}`;

        // Calculate total payout
        const neighborTotal =
          mainHit.neighborPayouts?.reduce((sum, p) => sum + p.amount, 0) ??
          0;
        const totalPayout =
          mainHit.payoutAmount +
          (wrongWayHit?.payoutAmount ?? 0) +
          (plusNHit?.payoutAmount ?? 0) +
          (plusNWrongWayHit?.payoutAmount ?? 0) +
          neighborTotal;

        return {
          label,
          shortLabel,
          mainHit,
          wrongWayHit,
          plusNHit,
          plusNWrongWayHit,
          totalPayout,
        };
      });

      // Also handle custom category hits (categoryId is set)
      const customHits = hitsData.filter(
        (h) => !h.isWrongWay && !h.isPlusN && h.categoryId
      );
      for (const customHit of customHits) {
        const category =
          boardData.payoutConfig?.customCategories?.find(
            (c) => c.id === customHit.categoryId
          );
        const label = category?.name ?? 'Custom';
        const shortLabel = label;
        const wrongWayHit = hitsData.find(
          (h) => h.parentHitId === customHit.id && h.isWrongWay
        );
        const totalPayout =
          customHit.payoutAmount + (wrongWayHit?.payoutAmount ?? 0);
        groups.push({
          label,
          shortLabel,
          mainHit: customHit,
          wrongWayHit,
          totalPayout,
        });
      }

      setWinnerGroups(groups);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load';
      if (message.includes('404') || message.includes('not found')) {
        setError('not_found');
      } else {
        setError('error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [boardSlug]);

  // Initial fetch
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Subscribe to real-time board events via SSE
  useBoardEvents(board?.id, {
    onHitChange: () => fetchData(),
    onBoardChange: () => fetchData(),
  });

  return { board, winnerGroups, isLoading, error };
};
